import { ReactNode } from 'react';
import { calculateMaterials } from '../../shared/calculations/materials';
import { FLOOR_LAYOUTS, getLayoutTemplate, isLegacyLayout, WALL_LAYOUTS } from '../../shared/layouts/templates';
import { FRIEZE_MIXES } from '../../shared/layouts/friezeMixes';
import { applyRoomPreset, ROOM_PRESETS } from '../../shared/presets/rooms';
import { ObjectType, ProjectState, TileCategory } from '../../shared/types';
import { useProjectStore } from '../store/useProjectStore';
import { fixtureSizeMm } from './fixtures';
import { Accordion, Field, SelectField, Toggle } from './Controls';
import { Icon, IconName } from './Icon';
import { TileUploadCard } from './TileUploadCard';

export type PanelId = 'project' | 'rooms' | 'room' | 'tiles' | 'layouts' | 'materials' | 'lighting' | 'objects' | 'fixtures';

const navigation: Array<{ id: PanelId; label: string; icon: IconName }> = [
  { id: 'project', label: 'Проект', icon: 'grid' },
  { id: 'rooms', label: 'Помещения', icon: 'room' },
  { id: 'room', label: 'Комната', icon: 'room' },
  { id: 'tiles', label: 'Плитка', icon: 'tile' },
  { id: 'layouts', label: 'Раскладка', icon: 'layout' },
  { id: 'materials', label: 'Расчёт', icon: 'save' },
  { id: 'lighting', label: 'Свет', icon: 'sun' },
  { id: 'objects', label: 'Объекты', icon: 'objects' },
  { id: 'fixtures', label: 'Сантехника', icon: 'room' },
];

const formatNumber = (value: number, digits = 2) =>
  new Intl.NumberFormat('ru-RU', { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(value);

const formatMoney = (value: number) =>
  `${Math.round(value).toLocaleString('ru-RU')} ₽`;

const tileCategories: TileCategory[] = ['floor', 'darkWall', 'lightWall', 'decor', 'border'];

const PanelBody = ({ children }: { children: ReactNode }) => <div className="panel-body">{children}</div>;

const NumberField = ({
  label,
  value,
  suffix,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  suffix?: string;
  min?: number;
  max?: number;
  onChange: (value: number) => void;
}) => (
  <Field
    label={label}
    type="number"
    value={value}
    suffix={suffix}
    min={min}
    max={max}
    onChange={(event) => onChange(Number(event.target.value))}
  />
);

interface LeftPanelProps {
  active: PanelId;
  onActiveChange: (active: PanelId) => void;
  savedProjects: Array<{ id: string; name: string; updatedAt: string }>;
  onOpenProject: (id: string) => void;
  onDeleteProject: (id: string) => void;
  onSave: () => void;
  onOpenProposal: () => void;
  /** Мобильная/планшетная шторка: панель выезжает поверх визуализации */
  open: boolean;
}

export const LeftPanel = ({
  active,
  onActiveChange,
  savedProjects,
  onOpenProject,
  onDeleteProject,
  onSave,
  onOpenProposal,
  open,
}: LeftPanelProps) => {
  const { project, updateProject, newProject } = useProjectStore();
  const selectedSettings = project.surfaces[project.selectedSurface];
  const hasMixedZone = getLayoutTemplate(selectedSettings.templateId).zones.some((zone) => zone.mix);
  const materials = calculateMaterials(project);

  const update = (updater: (draft: ProjectState) => void) => {
    updateProject((draft) => {
      updater(draft);
      return draft;
    });
  };

  const addObject = (type: ObjectType) => {
    const defaults = {
      door: { width: 800, height: 2050, x: 200, y: 0 },
      window: { width: 700, height: 600, x: 1200, y: 1200 },
      niche: { width: 600, height: 400, x: 1500, y: 900 },
    }[type];
    update((draft) => {
      const surfaceId = draft.selectedSurface === 'floor' ? 'front' : draft.selectedSurface;
      draft.objects.push({ id: crypto.randomUUID(), type, surfaceId, ...defaults });
    });
  };

  const applyPreset = (presetId: string) => {
    const preset = ROOM_PRESETS.find((item) => item.id === presetId);
    if (!preset) return;
    updateProject((draft) => applyRoomPreset(draft, preset));
    onActiveChange('layouts');
  };

  return (
    <aside className={`left-workspace${open ? ' open' : ''}`}>
      <nav className="rail" aria-label="Инструменты">
        <div className="rail-brand">K</div>
        <div className="rail-items">
          {navigation.map((item) => (
            <button
              key={item.id}
              type="button"
              className={active === item.id ? 'active' : ''}
              onClick={() => onActiveChange(item.id)}
              title={item.label}
            >
              <Icon name={item.icon} />
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      </nav>

      <section className="control-panel">
        {active === 'project' && (
          <PanelBody>
            <Accordion title="Проект">
              <Field
                label="Название"
                value={project.name}
                onChange={(event) => update((draft) => { draft.name = event.target.value; })}
              />
              <div className="button-grid">
                <button type="button" className="button secondary" onClick={newProject}>
                  <Icon name="plus" /> Новый
                </button>
                <button type="button" className="button primary" onClick={onSave}>
                  <Icon name="save" /> Сохранить
                </button>
              </div>
            </Accordion>
            <Accordion title="Последние">
              <div className="project-list">
                {savedProjects.length === 0 && <p className="empty-copy">Сохранённые проекты появятся здесь.</p>}
                {savedProjects.map((saved) => (
                  <div className="project-row" key={saved.id}>
                    <button type="button" className="project-open" onClick={() => onOpenProject(saved.id)}>
                      <span className="project-monogram">{saved.name.slice(0, 1).toUpperCase()}</span>
                      <span>
                        <strong>{saved.name}</strong>
                        <small>{new Date(saved.updatedAt).toLocaleString('ru-RU')}</small>
                      </span>
                      <Icon name="chevron" />
                    </button>
                    <button
                      type="button"
                      className="project-delete"
                      title="Удалить проект"
                      onClick={(event) => { event.stopPropagation(); onDeleteProject(saved.id); }}
                    >
                      <Icon name="trash" />
                    </button>
                  </div>
                ))}
              </div>
            </Accordion>
          </PanelBody>
        )}

        {active === 'rooms' && (
          <PanelBody>
            <p className="section-copy">
              Готовый сценарий подставляет габариты, раскладки, форматы плитки, сантехнику и запас.
              Дальше правите под клиента — визуализация и расчёт обновляются сами.
            </p>
            <Accordion title="Библиотека помещений">
              <div className="preset-list">
                {ROOM_PRESETS.map((preset) => {
                  const activePreset = project.room.width === preset.room.width &&
                    project.room.length === preset.room.length &&
                    project.name === preset.name;
                  return (
                    <button
                      type="button"
                      key={preset.id}
                      className={`preset-card${activePreset ? ' active' : ''}`}
                      onClick={() => applyPreset(preset.id)}
                    >
                      <span className="preset-head">
                        <strong>{preset.name}</strong>
                        <em>{preset.badge}</em>
                      </span>
                      <small>{preset.description}</small>
                      <span className="preset-tags">
                        {Object.entries(preset.templates).map(([surfaceId, templateId]) => (
                          <i key={surfaceId}>
                            {({
                              front: 'перед', left: 'лев', right: 'прав', back: 'зад', floor: 'пол',
                            } as Record<string, string>)[surfaceId as string] ?? surfaceId}
                            {' · '}
                            {[...WALL_LAYOUTS, ...FLOOR_LAYOUTS].find((layout) => layout.id === templateId)?.name ?? templateId}
                          </i>
                        ))}
                      </span>
                      {preset.hint && <em className="preset-hint">{preset.hint}</em>}
                      {activePreset && <Icon name="check" />}
                    </button>
                  );
                })}
              </div>
            </Accordion>
            <Accordion title="Сложные раскладки">
              <p className="section-copy">Ёлочка, диагональ, модульная, калейдоскоп, ступени, бордюры и декоры — в разделе «Раскладка».</p>
              <div className="layout-chips">
                {['floor-herringbone', 'floor-diagonal', 'floor-modular', 'floor-kaleidoscope', 'floor-stairs', 'wall-modular', 'wall-kaleidoscope', 'double-border'].map((layoutId) => {
                  const layout = [...WALL_LAYOUTS, ...FLOOR_LAYOUTS].find((item) => item.id === layoutId);
                  if (!layout) return null;
                  return (
                    <button
                      type="button"
                      key={layoutId}
                      className={selectedSettings.templateId === layoutId ? 'active' : ''}
                      onClick={() => update((draft) => {
                        const surfaceId = layout.kind === 'floor' ? 'floor' : draft.selectedSurface === 'floor' ? 'front' : draft.selectedSurface;
                        draft.selectedSurface = surfaceId;
                        draft.surfaces[surfaceId].templateId = layoutId;
                        onActiveChange('layouts');
                      })}
                    >
                      <span className={`layout-preview preview-${layout.id}`} />
                      {layout.name}
                    </button>
                  );
                })}
              </div>
            </Accordion>
          </PanelBody>
        )}

        {active === 'materials' && (
          <PanelBody>
            <p className="section-copy">
              Плитка, клей и затирка считаются автоматически по пяти поверхностям с учётом
              подрезки, формата и ширины шва.
            </p>
            <Accordion title="Расчёт материалов">
              <div className="material-totals">
                <span><small>Площадь</small><strong>{formatNumber(materials.area)} м²</strong></span>
                <span><small>Подрезка {materials.wastePercent}%</small><strong>{formatNumber(materials.areaWithWaste)} м²</strong></span>
                <span><small>Итого запас {formatNumber(materials.effectiveReservePercent, 1)}%</small><strong>{formatNumber(materials.areaWithReserve)} м²</strong></span>
                <span><small>Плитка</small><strong>{formatMoney(materials.tilesTotal)}</strong></span>
                <span><small>Клей и затирка</small><strong>{formatMoney(materials.consumablesTotal)}</strong></span>
                <span className="material-grand"><small>Итого</small><strong>{formatMoney(materials.total)}</strong></span>
              </div>
            </Accordion>

            <button type="button" className="button primary preset-cta" onClick={onOpenProposal}>
              <Icon name="download" /> Сформировать КП (PDF)
            </button>

            <Accordion title="Плитка по материалам">
              <div className="material-table">
                {materials.tiles.map((line) => (
                  <div className="material-row" key={line.category}>
                    <span className="material-name">
                      <strong>{line.name}</strong>
                      <small>{line.format} · {line.pieces} шт{line.packs > 0 ? ` (${line.packs} уп)` : ''} · {formatNumber(line.area)} м²</small>
                    </span>
                    <span className="material-sum">
                      {formatMoney(line.total)}
                      <small>{formatMoney(line.pricePerM2)}/м²</small>
                    </span>
                  </div>
                ))}
                {materials.tiles.length === 0 && <p className="empty-copy">Загрузите плитку в разделе «Плитка».</p>}
              </div>
            </Accordion>

            <Accordion title="Клей и затирка">
              <div className="material-table">
                {materials.consumables.map((line) => (
                  <div className="material-row" key={line.kind}>
                    <span className="material-name">
                      <strong>{line.kind === 'glue' ? 'Клей' : 'Затирка'}</strong>
                      <small>{line.perM2} кг/м² · {formatNumber(line.kg, 1)} кг · {line.packs} × {line.packSize} кг</small>
                      <small className="material-note">{line.note}</small>
                    </span>
                    <span className="material-sum">{formatMoney(line.total)}</span>
                  </div>
                ))}
              </div>
            </Accordion>

            <Accordion title="Поверхности">
              <div className="material-table">
                {materials.surfaces.map((surface) => (
                  <div className="material-row" key={surface.surfaceId}>
                    <span className="material-name">
                      <strong>{surface.label}</strong>
                      <small>{surface.width} × {surface.height} мм · {surface.templateName} · {surface.purchasedTiles} шт</small>
                    </span>
                    <span className="material-sum">{formatMoney(surface.total)}</span>
                  </div>
                ))}
              </div>
            </Accordion>

            <Accordion title="Параметры расчёта">
              <div className="field-grid">
                <NumberField
                  label="Толщина плитки, мм"
                  value={project.materials.tileThickness}
                  min={2}
                  max={60}
                  onChange={(value) => update((draft) => { draft.materials.tileThickness = Math.min(60, Math.max(2, value)); })}
                />
                <NumberField
                  label="На подрезку, %"
                  value={project.wastePercent}
                  min={0}
                  max={50}
                  onChange={(value) => update((draft) => { draft.wastePercent = Math.min(50, Math.max(0, value)); })}
                />
                <NumberField
                  label="Производственный запас, %"
                  value={project.materials.reservePercent}
                  min={0}
                  max={50}
                  onChange={(value) => update((draft) => { draft.materials.reservePercent = Math.min(50, Math.max(0, value)); })}
                />
                <NumberField
                  label="Мешок клея, кг"
                  value={project.materials.glueBagSize}
                  min={1}
                  max={100}
                  onChange={(value) => update((draft) => { draft.materials.glueBagSize = Math.min(100, Math.max(1, value)); })}
                />
                <NumberField
                  label="Упаковка затирки, кг"
                  value={project.materials.groutPackSize}
                  min={0.5}
                  max={50}
                  onChange={(value) => update((draft) => { draft.materials.groutPackSize = Math.min(50, Math.max(0.5, value)); })}
                />
                <NumberField
                  label="Клей, ₽/кг"
                  value={project.materials.gluePricePerKg}
                  min={0}
                  max={10000}
                  onChange={(value) => update((draft) => { draft.materials.gluePricePerKg = Math.max(0, value); })}
                />
                <NumberField
                  label="Затирка, ₽/кг"
                  value={project.materials.groutPricePerKg}
                  min={0}
                  max={10000}
                  onChange={(value) => update((draft) => { draft.materials.groutPricePerKg = Math.max(0, value); })}
                />
                {([
                  ['floor', 'Пол'],
                  ['lightWall', 'Светлая стена'],
                  ['darkWall', 'Тёмная стена'],
                  ['decor', 'Декор'],
                  ['border', 'Бордюр'],
                ] as Array<[TileCategory, string]>).map(([category, label]) => (
                  <NumberField
                    key={category}
                    label={`${label}, шт/упак`}
                    value={project.materials.tilesPerPack?.[category] ?? 0}
                    min={0}
                    max={10000}
                    onChange={(value) => update((draft) => {
                      draft.materials.tilesPerPack = {
                        ...(draft.materials.tilesPerPack ?? {}),
                        [category]: Math.min(10000, Math.max(0, Math.round(value))),
                      };
                    })}
                  />
                ))}
              </div>
              <p className="section-copy">
                Затирка считается по формуле ((A+B)/(A×B))×C×D×1,6, клей — по формату плитки и слою под гребёнку.
                Расход клея и затирки берётся по каждому формату отдельно (мелкая плитка — больше швов).
                «Шт/упак» округляет закупку плитки до целых упаковок; 0 — считать по площади.
              </p>
            </Accordion>
          </PanelBody>
        )}

        {active === 'room' && (
          <PanelBody>
            <p className="section-copy">От 300 до 20 000 мм — как в реальном помещении.</p>
            <Accordion title="Размеры комнаты">
              <div className="field-grid">
                <NumberField label="Ширина" value={project.room.width} suffix="мм" min={300} max={20000} onChange={(value) => update((draft) => { draft.room.width = Math.min(20000, Math.max(300, value)); })} />
                <NumberField label="Длина" value={project.room.length} suffix="мм" min={300} max={20000} onChange={(value) => update((draft) => { draft.room.length = Math.min(20000, Math.max(300, value)); })} />
                <NumberField label="Высота" value={project.room.height} suffix="мм" min={300} max={20000} onChange={(value) => update((draft) => { draft.room.height = Math.min(20000, Math.max(300, value)); })} />
              </div>
            </Accordion>
            <Accordion title="Поверхность">
              <div className="surface-grid">
                {([
                  ['front', 'Передняя'],
                  ['left', 'Левая'],
                  ['right', 'Правая'],
                  ['back', 'Задняя'],
                  ['floor', 'Пол'],
                ] as const).map(([id, label]) => (
                  <button
                    type="button"
                    key={id}
                    className={project.selectedSurface === id ? 'active' : ''}
                    onClick={() => update((draft) => { draft.selectedSurface = id; })}
                  >
                    <span className={`surface-icon surface-${id}`} />
                    {label}
                  </button>
                ))}
              </div>
            </Accordion>
          </PanelBody>
        )}

        {active === 'tiles' && (
          <PanelBody>
            <p className="section-copy">Загрузите фото одной плитки — текстура повторится в реальном масштабе.</p>
            <Accordion title="Материалы" count={tileCategories.length}>
              <div className="tile-cards">
                {tileCategories.map((category) => (
                  <TileUploadCard
                    key={category}
                    category={category}
                    tile={project.tiles[category]}
                    selected={project.selectedTile === category}
                    onSelect={() => update((draft) => { draft.selectedTile = category; })}
                    onUploaded={(tile) => update((draft) => {
                      draft.tiles[category] = tile;
                      draft.selectedTile = category;
                    })}
                  />
                ))}
              </div>
            </Accordion>
          </PanelBody>
        )}

        {active === 'layouts' && (
          <PanelBody>
            <p className="section-copy">Шаблон применяется отдельно к выбранной поверхности.</p>
            <Accordion title="Шаблоны раскладки">
              {isLegacyLayout(selectedSettings.templateId) && (
                <p className="section-copy">
                  Раскладка «{getLayoutTemplate(selectedSettings.templateId).name}» снята с выбора и сохранена только для старых проектов — выберите шаблон из списка.
                </p>
              )}
              <div className="layout-list">
                {(project.selectedSurface === 'floor' ? FLOOR_LAYOUTS : WALL_LAYOUTS).map((layout) => (
                  <button
                    type="button"
                    key={layout.id}
                    className={selectedSettings.templateId === layout.id ? 'active' : ''}
                    onClick={() => update((draft) => {
                      draft.surfaces[draft.selectedSurface].templateId = layout.id;
                    })}
                  >
                    <span className={`layout-preview preview-${layout.id}`} />
                    <span>
                      <strong>
                        {layout.name}
                        {layout.tag === 'complex' && <em className="layout-tag">сложная</em>}
                      </strong>
                      <small>{layout.description}</small>
                      {layout.hint && <small className="layout-hint">{layout.hint}</small>}
                    </span>
                    {selectedSettings.templateId === layout.id && <Icon name="check" />}
                  </button>
                ))}
              </div>
            </Accordion>
            <Accordion title="Параметры раскладки">
              <SelectField
                label="Начало раскладки"
                value={selectedSettings.alignment}
                onChange={(event) => update((draft) => {
                  draft.surfaces[draft.selectedSurface].alignment = event.target.value as typeof selectedSettings.alignment;
                })}
              >
                <option value="center">От центра</option>
                <option value="left">От левого края</option>
                <option value="right">От правого края</option>
                <option value="maxWhole">Максимум целых</option>
                <option value="custom">Своё смещение</option>
              </SelectField>
              {selectedSettings.alignment === 'custom' && (
                <NumberField label="Смещение" value={selectedSettings.customOffset} suffix="мм" onChange={(value) => update((draft) => { draft.surfaces[draft.selectedSurface].customOffset = value; })} />
              )}
              {hasMixedZone && (
                <SelectField
                  label="Комбинация фриза"
                  value={selectedSettings.friezeMix}
                  onChange={(event) => update((draft) => {
                    draft.surfaces[draft.selectedSurface].friezeMix = event.target.value;
                  })}
                >
                  {FRIEZE_MIXES.map((mix) => (
                    <option key={mix.id} value={mix.id}>{mix.label}</option>
                  ))}
                </SelectField>
              )}
              <NumberField label="Мин. подрезка" value={selectedSettings.minCutSize} suffix="мм" min={0} onChange={(value) => update((draft) => { draft.surfaces[draft.selectedSurface].minCutSize = value; })} />
              <Toggle label="Симметрия" checked={selectedSettings.symmetry} onChange={(checked) => update((draft) => { draft.surfaces[draft.selectedSurface].symmetry = checked; })} />
            </Accordion>
          </PanelBody>
        )}

        {active === 'lighting' && (
          <PanelBody>
            <Accordion title="Освещение">
              <div className="preset-row">
                {([
                  ['day', 'День'],
                  ['cloudy', 'Облачно'],
                  ['evening', 'Вечер'],
                  ['night', 'Ночь'],
                ] as const).map(([preset, label]) => (
                  <button
                    type="button"
                    key={preset}
                    className={project.lighting.preset === preset ? 'active' : ''}
                    onClick={() => update((draft) => {
                      draft.lighting.preset = preset;
                      draft.lighting.temperature = { day: 5200, cloudy: 6500, evening: 3200, night: 4200 }[preset];
                      draft.lighting.brightness = { day: 110, cloudy: 92, evening: 80, night: 48 }[preset];
                    })}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <RangeControl label="Яркость" value={project.lighting.brightness} min={20} max={200} suffix="%" onChange={(value) => update((draft) => { draft.lighting.brightness = value; })} />
              <RangeControl label="Интенсивность" value={project.lighting.intensity} min={0} max={100} suffix="%" onChange={(value) => update((draft) => { draft.lighting.intensity = value; })} />
              <RangeControl label="Температура" value={project.lighting.temperature} min={2700} max={6500} step={100} suffix="K" onChange={(value) => update((draft) => { draft.lighting.temperature = value; })} />
              <RangeControl label="Мягкость" value={project.lighting.softness} min={0} max={100} suffix="%" onChange={(value) => update((draft) => { draft.lighting.softness = value; })} />
              <SelectField label="Направление" value={project.lighting.direction} onChange={(event) => update((draft) => { draft.lighting.direction = event.target.value as ProjectState['lighting']['direction']; })}>
                <option value="left">Слева</option>
                <option value="right">Справа</option>
                <option value="top">Сверху</option>
                <option value="front">Спереди</option>
              </SelectField>
            </Accordion>
            <Accordion title="Натяжной потолок">
              <div className="ceiling-row">
                <span className="ceiling-color-label">Цвет потолка</span>
                <div className="color-swatches">
                  {['#f4f2ee', '#efe6d8', '#e5dccb', '#cdd8e2', '#d9c9d4', '#b7c4bd'].map((color) => (
                    <button
                      type="button"
                      key={color}
                      aria-label={`Цвет потолка ${color}`}
                      className={(project.ceiling?.color ?? '#f4f2ee') === color ? 'active' : ''}
                      style={{ backgroundColor: color }}
                      onClick={() => update((draft) => { draft.ceiling = { ...(draft.ceiling ?? { color: '#f4f2ee', glossy: true, spotlights: 4 }), color }; })}
                    />
                  ))}
                  <input
                    aria-label="Свой цвет потолка"
                    type="color"
                    value={project.ceiling?.color ?? '#f4f2ee'}
                    onChange={(event) => update((draft) => { draft.ceiling = { ...(draft.ceiling ?? { color: '#f4f2ee', glossy: true, spotlights: 4 }), color: event.target.value }; })}
                  />
                </div>
              </div>
              <Toggle
                label="Глянцевый потолок"
                checked={project.ceiling?.glossy ?? true}
                onChange={(checked) => update((draft) => { draft.ceiling = { ...(draft.ceiling ?? { color: '#f4f2ee', glossy: true, spotlights: 4 }), glossy: checked }; })}
              />
              <div className="spotlight-row">
                <span className="ceiling-color-label">Точечные светильники</span>
                <div className="segmented spotlights-seg">
                  {([0, 1, 2, 3, 4] as const).map((count) => (
                    <button
                      type="button"
                      key={count}
                      className={(project.ceiling?.spotlights ?? 0) === count ? 'active' : ''}
                      onClick={() => update((draft) => { draft.ceiling = { ...(draft.ceiling ?? { color: '#f4f2ee', glossy: true, spotlights: 4 }), spotlights: count }; })}
                    >
                      {count}
                    </button>
                  ))}
                </div>
              </div>
            </Accordion>
          </PanelBody>
        )}

        {active === 'objects' && (
          <PanelBody>
            <p className="section-copy">Проёмы исключаются из расчёта и визуализации плитки.</p>
            <Accordion title="Объекты">
              <div className="object-actions">
                {([
                  ['door', 'Дверь'],
                  ['window', 'Окно'],
                  ['niche', 'Ниша'],
                ] as const).map(([type, label]) => (
                  <button type="button" key={type} onClick={() => addObject(type)}>
                    <Icon name="plus" />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
              <div className="object-list">
                {project.objects.map((object, index) => {
                  const patchObject = (patch: Partial<typeof object>) =>
                    update((draft) => {
                      const target = draft.objects.find((item) => item.id === object.id);
                      if (target) Object.assign(target, patch);
                    });
                  return (
                    <div className="object-item object-item-editable" key={object.id}>
                      <div className="object-item-head">
                        <span className={`object-symbol ${object.type}`} />
                        <strong>{({ door: 'Дверь', window: 'Окно', niche: 'Ниша' })[object.type]} {index + 1}</strong>
                        <button type="button" aria-label="Удалить" onClick={() => update((draft) => { draft.objects = draft.objects.filter((item) => item.id !== object.id); })}>
                          <Icon name="x" />
                        </button>
                      </div>
                      <div className="object-edit-grid">
                        <NumberField label="Ширина, мм" value={object.width} min={100} onChange={(value) => patchObject({ width: Math.max(100, Math.round(value)) })} />
                        <NumberField label="Высота, мм" value={object.height} min={100} onChange={(value) => patchObject({ height: Math.max(100, Math.round(value)) })} />
                        <NumberField label="Отступ слева, мм" value={object.x} min={0} onChange={(value) => patchObject({ x: Math.max(0, Math.round(value)) })} />
                        <NumberField label="Низ от пола, мм" value={object.y} min={0} onChange={(value) => patchObject({ y: Math.max(0, Math.round(value)) })} />
                      </div>
                      <label className="field select-shell-field" style={{ display: 'block', width: '100%' }}>
                        <span style={{ display: 'block', marginBottom: '4px' }}>Стена</span>
                        <span className="input-shell select-shell" style={{ display: 'block', width: '100%' }}>
                          <select style={{ width: '100%' }} value={object.surfaceId} onChange={(event) => patchObject({ surfaceId: event.target.value as typeof object.surfaceId })}>
                            <option value="front">Передняя</option>
                            <option value="left">Левая</option>
                            <option value="right">Правая</option>
                            <option value="back">Задняя</option>
                          </select>
                        </span>
                      </label>
                      {object.type === 'door' && (
                        <label className="field select-shell-field" style={{ display: 'block', width: '100%' }}>
                          <span style={{ display: 'block', marginBottom: '4px' }}>Открывание</span>
                          <span className="input-shell select-shell" style={{ display: 'block', width: '100%' }}>
                            <select style={{ width: '100%' }} value={object.swing ?? 'right'} onChange={(event) => patchObject({ swing: event.target.value as 'left' | 'right' })}>
                              <option value="left">Влево</option>
                              <option value="right">Вправо</option>
                            </select>
                          </span>
                        </label>
                      )}
                    </div>
                  );
                })}
                {project.objects.length === 0 && <p className="empty-copy">Добавьте дверь, окно или нишу — они исключаются из расчёта плитки.</p>}
              </div>
            </Accordion>
          </PanelBody>
        )}

        {active === 'fixtures' && (
          <PanelBody>
            <p className="section-copy">Декоративные модели для 3D-презентации. В 3D-режиме их можно тянуть мышью — модель сама прилипает к стенам, R или двойной клик — поворот, Delete — убрать. В расчёт плитки не входят.</p>
            <Accordion title="Сантехника">
              <div className="object-actions">
                {([
                  ['bath', 'Ванна'],
                  ['toilet', 'Унитаз'],
                  ['sink', 'Раковина'],
                ] as const).map(([kind, label]) => (
                  <button
                    type="button"
                    key={kind}
                    onClick={() => update((draft) => {
                      const size = fixtureSizeMm[kind];
                      // Ставим в ближний свободный угол комнаты
                      draft.fixtures = [
                        ...(draft.fixtures ?? []),
                        {
                          id: crypto.randomUUID(),
                          kind,
                          x: Math.round(Math.max(size.w / 2, Math.min(project.room.width - size.w / 2, size.w / 2 + 100))),
                          z: Math.round(Math.max(size.d / 2, Math.min(project.room.length - size.d / 2, project.room.length - size.d / 2 - 100))),
                          rotation: 0 as const,
                        },
                      ];
                    })}
                  >
                    <Icon name="plus" />
                    <span>{label}</span>
                  </button>
                ))}
              </div>
              <div className="object-list">
                {(project.fixtures ?? []).map((fixture, index) => {
                  const patchFixture = (patch: Partial<typeof fixture>) =>
                    update((draft) => {
                      const target = (draft.fixtures ?? []).find((item) => item.id === fixture.id);
                      if (target) Object.assign(target, patch);
                    });
                  return (
                    <div className="object-item object-item-editable" key={fixture.id}>
                      <div className="object-item-head">
                        <span className={`object-symbol ${fixture.kind}`} />
                        <strong>{({ bath: 'Ванна', toilet: 'Унитаз', sink: 'Раковина' })[fixture.kind]} {index + 1}</strong>
                        <button
                          type="button"
                          aria-label="Удалить"
                          onClick={() => update((draft) => { draft.fixtures = (draft.fixtures ?? []).filter((item) => item.id !== fixture.id); })}
                        >
                          <Icon name="x" />
                        </button>
                      </div>
                      <div className="object-edit-grid">
                        <NumberField
                          label="По ширине, мм"
                          value={fixture.x}
                          min={0}
                          onChange={(value) => patchFixture({ x: Math.max(0, Math.round(value)) })}
                        />
                        <NumberField
                          label="По длине, мм"
                          value={fixture.z}
                          min={0}
                          onChange={(value) => patchFixture({ z: Math.max(0, Math.round(value)) })}
                        />
                      </div>
                      <label className="field select-shell-field" style={{ display: 'block', width: '100%' }}>
                        <span style={{ display: 'block', marginBottom: '4px' }}>Поворот</span>
                        <span className="input-shell select-shell" style={{ display: 'block', width: '100%' }}>
                          <select
                            style={{ width: '100%' }}
                            value={fixture.rotation}
                            onChange={(event) => patchFixture({ rotation: Number(event.target.value) as 0 | 90 | 180 | 270 })}
                          >
                            <option value={0}>0°</option>
                            <option value={90}>90°</option>
                            <option value={180}>180°</option>
                            <option value={270}>270°</option>
                          </select>
                        </span>
                      </label>
                    </div>
                  );
                })}
                {(project.fixtures ?? []).length === 0 && (
                  <p className="empty-copy">Добавьте ванну, унитаз или раковину — увидите их в 3D-режиме.</p>
                )}
              </div>
            </Accordion>
          </PanelBody>
        )}
      </section>
    </aside>
  );
};

const RangeControl = ({
  label,
  value,
  min,
  max,
  step = 1,
  suffix,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix: string;
  onChange: (value: number) => void;
}) => (
  <label className="range-control">
    <span><strong>{label}</strong><output>{value}{suffix}</output></span>
    <input type="range" value={value} min={min} max={max} step={step} onChange={(event) => onChange(Number(event.target.value))} />
  </label>
);